import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { LogOut, Save, Plus, Trash2, FileDown, ArrowUpRight } from "lucide-react";

const TABS = [
    { key: "athlete", label: "Athlete" },
    { key: "featuredVideo", label: "Featured Video" },
    { key: "videoLibrary", label: "Video Library" },
    { key: "biography", label: "Biography" },
    { key: "academics", label: "Academics" },
    { key: "collegeFit", label: "College Fit" },
    { key: "schedule", label: "Schedule" },
    { key: "externalProfiles", label: "External Profiles" },
    { key: "contacts", label: "Contacts" },
    { key: "accomplishments", label: "Accomplishments" },
    { key: "portraits", label: "Portraits" },
    { key: "analytics", label: "Analytics" },
];

function Login({ onOk }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const submit = async (e) => {
        e.preventDefault();
        try {
            const r = await api.post("/auth/login", { email, password });
            localStorage.setItem("admin_token", r.data.token);
            toast.success("Signed in");
            onOk();
        } catch {
            toast.error("Invalid credentials");
        }
    };
    return (
        <div className="min-h-screen flex items-center justify-center px-6 bg-black">
            <form onSubmit={submit} className="w-full max-w-md border border-white/10 p-8 bg-black" data-testid="login-form">
                <div className="chapter-num">Administrator</div>
                <h1 className="font-mono uppercase text-3xl mt-2">Sign In</h1>
                <label className="block mt-6">
                    <span className="chapter-num">Email</span>
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-2 w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm outline-none focus:border-brand-gold"
                        data-testid="login-email"
                    />
                </label>
                <label className="block mt-4">
                    <span className="chapter-num">Password</span>
                    <input
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="mt-2 w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm outline-none focus:border-brand-gold"
                        data-testid="login-password"
                    />
                </label>
                <button className="btn-gold mt-6 w-full justify-center" data-testid="login-submit">
                    Sign In
                </button>
                <a href="/" className="block mt-4 chapter-num text-white/60 hover:text-brand-gold">← Back to site</a>
            </form>
        </div>
    );
}

const Field = ({ label, value, onChange, textarea, type = "text" }) => (
    <label className="block">
        <span className="chapter-num">{label}</span>
        {textarea ? (
            <textarea
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                rows={5}
                className="mt-2 w-full bg-transparent border border-white/10 p-3 font-mono text-sm text-white outline-none focus:border-brand-gold"
            />
        ) : (
            <input
                type={type}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="mt-2 w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white outline-none focus:border-brand-gold"
            />
        )}
    </label>
);

export default function Admin() {
    const [authed, setAuthed] = useState(!!localStorage.getItem("admin_token"));
    const [content, setContent] = useState(null);
    const [tab, setTab] = useState("athlete");
    const [saving, setSaving] = useState(false);
    const [submissions, setSubmissions] = useState([]);

    const load = async () => {
        try {
            await api.get("/auth/me");
        } catch {
            localStorage.removeItem("admin_token");
            setAuthed(false);
            return;
        }
        const r = await api.get("/content");
        setContent(r.data);
        try {
            const s = await api.get("/contact/submissions");
            setSubmissions(s.data);
        } catch (e) { /* no submissions */ }
    };

    useEffect(() => {
        if (authed) load();
    }, [authed]);

    const save = async () => {
        setSaving(true);
        try {
            const r = await api.put("/content", content);
            setContent(r.data);
            toast.success("Saved");
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const logout = () => {
        localStorage.removeItem("admin_token");
        setAuthed(false);
    };

    if (!authed) return <Login onOk={() => setAuthed(true)} />;
    if (!content) return <div className="p-10 font-mono">Loading…</div>;

    const set = (key, value) => setContent((c) => ({ ...c, [key]: value }));
    const patch = (key, sub) => setContent((c) => ({ ...c, [key]: { ...c[key], ...sub } }));

    return (
        <div className="min-h-screen bg-black">
            <header className="border-b border-white/10 sticky top-0 bg-black/90 backdrop-blur-xl z-40">
                <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-14 flex items-center justify-between">
                    <div className="font-mono uppercase text-sm tracking-[0.25em]">
                        <span className="text-brand-gold">●</span> Admin / Recruiting CMS
                    </div>
                    <div className="flex items-center gap-2">
                        <a href="/" target="_blank" rel="noreferrer" className="btn-outline">
                            View Site <ArrowUpRight size={14} />
                        </a>
                        <a href={`${API}/resume.pdf`} target="_blank" rel="noreferrer" className="btn-outline">
                            <FileDown size={14} /> Résumé PDF
                        </a>
                        <button onClick={save} disabled={saving} className="btn-gold" data-testid="save-btn">
                            <Save size={14} /> {saving ? "Saving…" : "Save"}
                        </button>
                        <button onClick={logout} className="btn-outline" data-testid="logout-btn">
                            <LogOut size={14} />
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
                <aside className="lg:col-span-3">
                    <div className="chapter-num mb-3">Sections</div>
                    <nav className="border border-white/10">
                        {TABS.map((t) => (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                className={`w-full text-left px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] border-b border-white/10 last:border-b-0 ${
                                    tab === t.key ? "bg-brand-gold text-black" : "hover:bg-white/5"
                                }`}
                                data-testid={`tab-${t.key}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </nav>

                    <div className="mt-8 chapter-num mb-3">Submissions ({submissions.length})</div>
                    <div className="border border-white/10 max-h-96 overflow-auto">
                        {submissions.length === 0 && (
                            <div className="p-4 chapter-num text-white/40">No submissions yet.</div>
                        )}
                        {submissions.map((s) => (
                            <div key={s.id} className="p-3 border-b border-white/10 last:border-b-0">
                                <div className="font-mono text-sm">{s.coachName}</div>
                                <div className="chapter-num">{s.college}</div>
                                <div className="chapter-num text-white/50">{s.email}</div>
                            </div>
                        ))}
                    </div>
                </aside>

                <main className="lg:col-span-9 space-y-6">
                    {tab === "athlete" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {[
                                ["firstName", "First Name"],
                                ["lastName", "Last Name"],
                                ["tagline", "Tagline"],
                                ["recruitingStatus", "Recruiting Status"],
                                ["graduationYear", "Graduation Year"],
                                ["highSchool", "High School"],
                                ["location", "Location"],
                                ["clubName", "Club Name"],
                                ["gpa", "GPA"],
                                ["actScore", "ACT Score"],
                                ["currentWeight", "Current Weight"],
                                ["projectedWeight", "Projected Weight"],
                                ["height", "Height"],
                                ["varsityYears", "Varsity Years"],
                                ["yearsWrestling", "Years Wrestling"],
                                ["careerRecord", "Career Record"],
                                ["hsRecord", "HS Record (e.g. 13-4)"],
                                ["nationalRecord", "National/Open Record (e.g. 1-6)"],
                                ["nationalRecordLabel", "National Record Label"],
                                ["majorInterest", "Intended Major"],
                                ["ncaaStatus", "NCAA Status"],
                            ].map(([k, l]) => (
                                <Field key={k} label={l} value={content.athlete[k]} onChange={(v) => patch("athlete", { [k]: v })} />
                            ))}
                        </div>
                    )}

                    {tab === "featuredVideo" && (
                        <div className="space-y-5">
                            <Field label="Title" value={content.featuredVideo.title} onChange={(v) => patch("featuredVideo", { title: v })} />
                            <Field label="URL (MP4 or embed)" value={content.featuredVideo.url} onChange={(v) => patch("featuredVideo", { url: v })} />
                            <Field label="Description" value={content.featuredVideo.description} onChange={(v) => patch("featuredVideo", { description: v })} textarea />
                        </div>
                    )}

                    {tab === "videoLibrary" && (
                        <ListEditor
                            items={content.videoLibrary}
                            onChange={(arr) => set("videoLibrary", arr)}
                            fields={[
                                ["title", "Title"],
                                ["url", "URL"],
                                ["type", "Type (highlight | full_match)"],
                                ["weightClass", "Weight Class"],
                                ["event", "Event"],
                                ["result", "Result"],
                                ["description", "Description"],
                            ]}
                            newItem={() => ({ id: crypto.randomUUID(), title: "", url: "", type: "highlight", weightClass: "132", event: "", result: "", description: "" })}
                        />
                    )}

                    {tab === "biography" && (
                        <Field label="Biography" value={content.biography} onChange={(v) => set("biography", v)} textarea />
                    )}

                    {tab === "academics" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {["gpa", "actScore", "graduationYear", "intendedMajor", "ncaaStatus"].map((k) => (
                                <Field key={k} label={k} value={content.academics[k]} onChange={(v) => patch("academics", { [k]: v })} />
                            ))}
                            <div className="md:col-span-2">
                                <Field label="Academic Interests" value={content.academics.academicInterests} onChange={(v) => patch("academics", { academicInterests: v })} textarea />
                            </div>
                            <div className="md:col-span-2">
                                <Field label="Leadership" value={content.academics.leadership} onChange={(v) => patch("academics", { leadership: v })} textarea />
                            </div>
                            <div className="md:col-span-2">
                                <Field label="Qualities (comma-separated)" value={(content.academics.qualities || []).join(", ")} onChange={(v) => patch("academics", { qualities: v.split(",").map((x) => x.trim()).filter(Boolean) })} />
                            </div>
                            <div className="md:col-span-2">
                                <Field label="Transcript Statement" value={content.academics.transcriptStatement} onChange={(v) => patch("academics", { transcriptStatement: v })} textarea />
                            </div>
                        </div>
                    )}

                    {tab === "collegeFit" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="md:col-span-2">
                                <Field label="Divisions (comma-separated)" value={(content.collegeFit.divisions || []).join(", ")} onChange={(v) => patch("collegeFit", { divisions: v.split(",").map((x) => x.trim()).filter(Boolean) })} />
                            </div>
                            {["academicInterests", "geographic", "campusSize", "wrestlingCulture", "academicSupport", "careerGoals"].map((k) => (
                                <Field key={k} label={k} value={content.collegeFit[k]} onChange={(v) => patch("collegeFit", { [k]: v })} textarea />
                            ))}
                        </div>
                    )}

                    {tab === "schedule" && (
                        <ListEditor
                            items={content.schedule}
                            onChange={(arr) => set("schedule", arr)}
                            fields={[
                                ["eventName", "Event Name"],
                                ["date", "Date (YYYY-MM-DD)"],
                                ["location", "Location"],
                                ["weightClass", "Weight Class"],
                                ["type", "Type"],
                                ["livestream", "Livestream / Result URL"],
                                ["notes", "Notes"],
                            ]}
                            newItem={() => ({ id: crypto.randomUUID(), eventName: "", date: "", location: "", weightClass: "132", type: "tournament", livestream: "", notes: "" })}
                        />
                    )}

                    {tab === "externalProfiles" && (
                        <ListEditor
                            items={content.externalProfiles}
                            onChange={(arr) => set("externalProfiles", arr)}
                            fields={[["platform", "Platform"], ["url", "URL"]]}
                            newItem={() => ({ platform: "", url: "" })}
                            keyProp="platform"
                        />
                    )}

                    {tab === "contacts" && (
                        <div className="space-y-6">
                            {["athlete", "parent", "hsCoach", "clubCoach"].map((k) => (
                                <div key={k} className="border border-white/10 p-5">
                                    <div className="chapter-num mb-3">{k}</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {["name", "role", "email", "phone"].map((f) => (
                                            <Field
                                                key={f}
                                                label={f}
                                                value={content.contacts[k][f]}
                                                onChange={(v) => patch("contacts", { [k]: { ...content.contacts[k], [f]: v } })}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {tab === "accomplishments" && (
                        <ListEditor
                            items={content.accomplishments}
                            onChange={(arr) => set("accomplishments", arr)}
                            fields={[["title", "Title"], ["year", "Year"]]}
                            newItem={() => ({ id: crypto.randomUUID(), title: "", year: "" })}
                        />
                    )}

                    {tab === "portraits" && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {["hero", "about", "contact"].map((k) => (
                                <div key={k} className="border border-white/10 p-4">
                                    <div className="chapter-num">{k}</div>
                                    <img src={content.portraits[k]} alt={k} className="w-full aspect-[3/4] object-cover my-3" />
                                    <Field
                                        label="Image URL"
                                        value={content.portraits[k]}
                                        onChange={(v) => patch("portraits", { [k]: v })}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {tab === "analytics" && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                            {Object.entries(content.analytics || {}).map(([k, v]) => (
                                <div key={k} className="p-6 bg-black">
                                    <div className="chapter-num">{k}</div>
                                    <div className="font-mono text-4xl mt-3 text-brand-gold">{v}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}

function ListEditor({ items, onChange, fields, newItem, keyProp = "id" }) {
    return (
        <div className="space-y-4">
            <button
                onClick={() => onChange([...(items || []), newItem()])}
                className="btn-outline"
            >
                <Plus size={14} /> Add Item
            </button>
            {(items || []).map((item, i) => (
                <div key={item[keyProp] || i} className="border border-white/10 p-5 relative">
                    <button
                        onClick={() => onChange(items.filter((_, j) => j !== i))}
                        className="absolute top-3 right-3 text-white/50 hover:text-red-400"
                    >
                        <Trash2 size={14} />
                    </button>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {fields.map(([k, l]) => (
                            <div key={k} className={k === "description" || k === "notes" ? "md:col-span-2" : ""}>
                                <Field
                                    label={l}
                                    value={item[k]}
                                    onChange={(v) => {
                                        const next = [...items];
                                        next[i] = { ...next[i], [k]: v };
                                        onChange(next);
                                    }}
                                    textarea={k === "description" || k === "notes"}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
